/**
 * VireoHR - Authentication Context
 * Firebase Authentication with Firestore user profiles
 * Manages user authentication state and role-based access
 * Supports: owner, employee, admin, manager roles
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import {
  signInWithEmail,
  registerWithEmail,
  signOut as firebaseSignOut,
  onAuthStateChange,
  initializeAuthPersistence
} from '@/services/firebaseAuthService';

export type UserRole = 'owner' | 'employee' | 'admin' | 'manager' | 'super_admin';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  tenantId?: string;
  tenantName?: string;
  branchIds?: string[]; // For managers: assigned branches
  managerLocationIds?: string[]; // For managers: locations they can manage
  isManager?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  registerUser: (email: string, password: string, role: UserRole, name: string, branchIds?: string[]) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize Firebase Auth persistence
    initializeAuthPersistence();

    // Listen to auth state changes
    const unsubscribe = onAuthStateChange((user) => {
      setUser(user);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const userProfile = await signInWithEmail(email, password);
      setUser(userProfile);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    firebaseSignOut();
    setUser(null);
  };

  const registerUser = async (
    email: string,
    password: string,
    role: UserRole,
    name: string,
    branchIds?: string[]
  ) => {
    try {

      const tenantId = import.meta.env.DEV ? 'tenant1' : undefined;
      const tenantName = import.meta.env.DEV ? 'Demo Business' : undefined;

      if (!import.meta.env.DEV) {
        throw new Error('Self-registration is disabled in production. Please contact an administrator.');
      }
      const newUser = await registerWithEmail(
        email,
        password,
        name,
        role,
        tenantId,
        tenantName,
        branchIds
      );
      setUser(newUser);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    registerUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
